// Settings › Symbols (D35, D73, D79; Console D, APS-319): the in-app images of a release that
// have no symbols, and how to upload them: dSYMs for iOS, R8 mappings and NDK debug files for
// Android (the io.apsio.gradle plugin, or the apsio CLI), React Native source maps for either, in
// one code window, with the docs at docs.apsio.io/symbols.
import { useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { appsQuery, lastDays, missingSymbolsQuery, releasesQuery } from '../api/queries'
import type { MissingSymbol, Platform } from '../api/types'
import { AffectedIssues } from '../components/AffectedIssues'
import {
  type CodeTab,
  CodeTabs,
  copyText,
  docTabs,
  EmptyNote,
  PageHead,
  ScrollX,
  Section,
  Status,
  useToast,
} from '../components/d'
import { Icon } from '../components/icons'
import { appLabel } from '../components/Shell'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { type SymbolFile, symbolFileTabs } from '../lib/docSnippets'
import { releasesNewestFirst } from '../lib/releases'
import { UploadTokens } from './UploadTokens'

const DOCS = 'https://docs.apsio.io/symbols'
/** The docs' symbols guide, linking every platform's upload page. */
export const SYMBOLS_GUIDE = 'https://docs.apsio.io/concepts/symbols'
export const SYMBOL_DOCS = {
  upload: `${DOCS}/upload`,
  xcode: `${DOCS}/xcode`,
  ci: `${DOCS}/ci`,
  android: `${DOCS}/android`,
  ndk: `${DOCS}/android-ndk`,
  reactNative: `${DOCS}/react-native`,
  tokens: `${DOCS}/upload#upload-tokens`,
} as const

/** An image's name and architecture, whichever the release reported. */
export const imageLabel = (m: { image_name: string; arch: string }): string =>
  [m.image_name, m.arch].filter(Boolean).join(' · ')

/** Rows the API classifies as Apple binaries bundled in the app, besides its own. */
export const bundledFrameworks = (rows: { kind: string }[]): number =>
  rows.filter((r) => r.kind === 'framework').length

/** Rows the API classifies as Android native libraries, the app's or a dependency's. */
export const nativeLibraries = (rows: { kind: string }[]): number =>
  rows.filter((r) => r.kind === 'native_library').length

/**
 * Where the missing files come from, under a list that has them: a bundled framework's dSYM,
 * and a native library's debug file (the API cannot tell the app's own from a dependency's).
 */
export function MissingSymbolsHints({ rows }: { rows: { kind: string }[] }) {
  const { rich } = useI18n()
  const hints = [
    bundledFrameworks(rows) > 0 && ('symbols.frameworkHint' as const),
    nativeLibraries(rows) > 0 && ('symbols.nativeLibraryHint' as const),
  ].filter((h) => !!h)
  return hints.map((h) => (
    <p key={h} className="dim" style={{ fontSize: 12.5 }}>
      {rich(h)}
    </p>
  ))
}

function DocLink({ href, children }: { href: string; children: string }) {
  return (
    <a className="ul" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  )
}

/** A debug id shortened for a table (`3f2a61c4…91c4`); the full id is its title. */
export const shortDebugId = (id: string): string =>
  id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id

/** What a missing image needs uploaded, from its kind and its app's platform. */
export function neededFile(
  kind: MissingSymbol['kind'],
  platform: Platform | undefined,
): 'dsym' | 'mapping' | 'ndk' | 'sourcemap' {
  if (kind === 'native_library') return 'ndk'
  if (kind === 'source_map') return 'sourcemap'
  // Android has no Apple binaries: anything else of an Android app is its build's R8 mapping.
  return platform === 'android' ? 'mapping' : 'dsym'
}

/** The kind as the page names it: an Android app's build, never an Apple framework. */
export const shownKind = (
  kind: MissingSymbol['kind'],
  platform: Platform | undefined,
): MissingSymbol['kind'] => (platform === 'android' && kind === 'framework' ? 'app' : kind)

/** Where a missing file comes from, under what it needs (the mock's second line). */
export function fileSource(
  kind: MissingSymbol['kind'],
  platform: Platform | undefined,
): 'archive' | 'vendor' | 'mapping' | 'ndk' | 'bundle' {
  const need = neededFile(kind, platform)
  if (need === 'mapping') return 'mapping'
  if (need === 'ndk') return 'ndk'
  if (need === 'sourcemap') return 'bundle'
  return kind === 'framework' ? 'vendor' : 'archive'
}

export const SOURCE_KEY = {
  archive: 'symbols.from.archive',
  vendor: 'symbols.from.vendor',
  mapping: 'symbols.from.mapping',
  ndk: 'symbols.from.ndk',
  bundle: 'symbols.from.bundle',
} as const

/** The issues whose frames stay raw until the missing files arrive. */
export const rawIssues = (rows: { issues: readonly string[] }[]): number =>
  new Set(rows.flatMap((r) => r.issues)).size

export const NEED_KEY = {
  dsym: 'symbols.need.dsym',
  mapping: 'symbols.need.mapping',
  ndk: 'symbols.need.ndk',
  sourcemap: 'symbols.need.sourcemap',
} as const

export const KIND_KEY = {
  app: 'symbols.kind.app',
  framework: 'symbols.framework',
  native_library: 'symbols.kind.nativeLibrary',
  source_map: 'symbols.kind.sourceMap',
} as const

const NOTE: Record<SymbolFile, MessageId[]> = {
  dsym: ['symbols.iosBody'],
  mapping: ['symbols.androidBody', 'symbols.androidCli'],
  ndk: ['symbols.ndkBody', 'symbols.ndkCli'],
  sourcemap: ['symbols.rnBody', 'symbols.rnCli'],
}

/** The upload tabs the project's platforms need (every tab when it has no apps yet), from the docs. */
/** The upload tabs; each tab's note is the hint above the window, its commands set in code (APS-385). */
export function uploadTabs(
  i18n: Pick<Translator, 'rich'>,
  platforms: ReadonlySet<Platform>,
): CodeTab[] {
  return docTabs(symbolFileTabs(platforms), (x) =>
    NOTE[x.id as SymbolFile].map((k, i) => (
      <Fragment key={k}>
        {i > 0 ? ' ' : null}
        {i18n.rich(k)}
      </Fragment>
    )),
  )
}

export function SymbolsSettings({
  orgId,
  projectId,
  crumb,
  title,
}: {
  orgId: string
  projectId: string
  crumb: string
  title: string
}) {
  const { t, rich } = useI18n()
  const apps = useQuery(appsQuery(orgId, projectId))
  const now = Math.floor(Date.now() / 60_000) * 60_000
  const releases = useQuery(releasesQuery(projectId, lastDays(30, now), ['window', 30, now]))
  const names = [...new Set(releasesNewestFirst(releases.data ?? []).map((r) => r.release))]
  const [picked, setPicked] = useState<string>()
  const release = picked ?? names[0]
  const missing = useQuery({
    ...missingSymbolsQuery(projectId, release ?? ''),
    enabled: !!release,
  })
  const { toast, show } = useToast()
  const platforms = new Set<Platform>((apps.data ?? []).map((a) => a.platform))
  const tabs = uploadTabs({ rich }, platforms)
  const appOf = (id: string) => apps.data?.find((x) => x.id === id)
  const first = tabs[0]
  const rows = missing.data?.missing ?? []

  return (
    <main id="main" className="pg se-pg symbols">
      <PageHead
        crumb={crumb}
        title={title}
        meta={
          release && missing.data ? (
            <>
              <span translate="no">{release}</span>
              {' · '}
              {missing.data.images === 0
                ? t('symbols.meta.none')
                : rows.length === 0
                  ? t('symbols.meta.all', { images: missing.data.images })
                  : t('symbols.meta.missing', { count: rows.length })}
              {rawIssues(rows) > 0
                ? ` · ${t('symbols.meta.issues', { count: rawIssues(rows) })}`
                : ''}
            </>
          ) : undefined
        }
        actions={
          first ? (
            <button
              type="button"
              className="btn"
              onClick={async () =>
                show((await copyText(first.copy)) ? t('symbols.copied') : t('d.code.copyFailed'))
              }
            >
              <Icon name="copy" className="se-i" />
              {t('symbols.copyCommand')}
            </button>
          ) : undefined
        }
      />
      <div className="se-main">
        <Section
          title={t('symbols.missing')}
          meta={t('symbols.byApp')}
          right={
            names.length > 0 ? (
              <select
                className="select"
                aria-label={t('issues.chip.release')}
                value={release}
                onChange={(e) => setPicked(e.target.value)}
                translate="no"
              >
                {names.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            ) : undefined
          }
        >
          {releases.isPending ? (
            <Loading />
          ) : names.length === 0 ? (
            <p className="se-p">{t('symbols.noReleases')}</p>
          ) : !release ? null : missing.isPending ? (
            <Loading />
          ) : missing.isError ? (
            <ErrorState error={missing.error} retry={() => missing.refetch()} />
          ) : missing.data.images === 0 ? (
            <EmptyNote title={t('releases.symbols.none')}>
              {t('releases.symbols.noneBody')}
            </EmptyNote>
          ) : rows.length === 0 ? (
            <p className="se-p" style={{ paddingTop: 12 }}>
              <Status s="ok" label={t('releases.symbols.all', { images: missing.data.images })} />
            </p>
          ) : (
            <ScrollX label={t('table.of', { name: t('symbols.missing') })}>
              <table className="t" aria-label={t('symbols.missing')}>
                <thead>
                  <tr>
                    <th className="f" scope="col">
                      {t('symbols.col.image')}
                    </th>
                    <th scope="col">{t('symbols.col.debugId')}</th>
                    <th scope="col">{t('symbols.col.app')}</th>
                    <th scope="col">{t('symbols.col.build')}</th>
                    <th scope="col">{t('symbols.col.needs')}</th>
                    <th className="last" scope="col">
                      {t('symbols.col.issues')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const app = appOf(m.app_id)
                    return (
                      <tr key={`${m.app_id}-${m.debug_id}`}>
                        <td className="f">
                          {imageLabel(m) ? (
                            <b className="ttl" translate="no">
                              {imageLabel(m)}
                            </b>
                          ) : (
                            <b className="ttl dim">{t('symbols.unnamed')}</b>
                          )}
                          <span className="t2">
                            {t(KIND_KEY[shownKind(m.kind, app?.platform)])}
                          </span>
                        </td>
                        <td>
                          <code title={m.debug_id} translate="no">
                            {shortDebugId(m.debug_id)}
                          </code>
                        </td>
                        <td>{app ? appLabel(app) : <code translate="no">{m.app_id}</code>}</td>
                        <td>
                          <code translate="no">{m.build}</code>
                        </td>
                        <td>
                          <Status s="miss" label={t(NEED_KEY[neededFile(m.kind, app?.platform)])} />
                          <span className="t2">
                            {t(SOURCE_KEY[fileSource(m.kind, app?.platform)])}
                          </span>
                        </td>
                        <td className="aff last">
                          <AffectedIssues
                            orgId={orgId}
                            projectId={projectId}
                            issues={m.issues}
                            count={m.issue_count}
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </ScrollX>
          )}
        </Section>
        <div className="grid2">
          <Section
            title={t('symbols.upload')}
            right={<DocLink href={SYMBOLS_GUIDE}>{t('setup.sdk.guide')}</DocLink>}
          >
            {tabs.length > 0 ? (
              <CodeTabs label={t('symbols.upload')} tabs={tabs} notesAbove />
            ) : null}
          </Section>
          <aside className="sections" aria-label={t('symbols.side')}>
            <UploadTokens orgId={orgId} projectId={projectId} />
            <Section title={t('symbols.expiryTitle')} minor>
              <p className="se-p">{t('symbols.expiry')}</p>
            </Section>
          </aside>
        </div>
      </div>
      {toast}
    </main>
  )
}
